import { FileUploadZone, type UploadedFile } from './FileUploadZone';

interface Competitor {
  name: string;
  website: string;
  differentiators: string;
  pricing: string;
}

interface CompetitorsData {
  competitors: Competitor[];
}

interface CompetitorsSectionProps {
  data: CompetitorsData;
  onChange: (data: CompetitorsData) => void;
  questionnaireId: string;
  uploads: UploadedFile[];
  onUploadsChange: (uploads: UploadedFile[]) => void;
}

const emptyCompetitor: Competitor = { name: '', website: '', differentiators: '', pricing: '' };

export function CompetitorsSection({
  data,
  onChange,
  questionnaireId,
  uploads,
  onUploadsChange,
}: CompetitorsSectionProps) {
  const updateCompetitor = (index: number, field: keyof Competitor, value: string) => {
    const updated = [...data.competitors];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ competitors: updated });
  };

  const addCompetitor = () => {
    if (data.competitors.length >= 10) return;
    onChange({ competitors: [...data.competitors, { ...emptyCompetitor }] });
  };

  const removeCompetitor = (index: number) => {
    onChange({ competitors: data.competitors.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Add your top competitors and describe what differentiates you from each one.
      </p>

      {data.competitors.map((comp, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">Competitor {index + 1}</h4>
            <button
              type="button"
              onClick={() => removeCompetitor(index)}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Remove
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                type="text"
                value={comp.name}
                onChange={(e) => updateCompetitor(index, 'name', e.target.value)}
                placeholder="Competitor name"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Website</label>
              <input
                type="url"
                value={comp.website}
                onChange={(e) => updateCompetitor(index, 'website', e.target.value)}
                placeholder="https://"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Differentiators</label>
            <textarea
              value={comp.differentiators}
              onChange={(e) => updateCompetitor(index, 'differentiators', e.target.value)}
              rows={2}
              placeholder="How do you differ from this competitor?"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Their Pricing</label>
            <input
              type="text"
              value={comp.pricing}
              onChange={(e) => updateCompetitor(index, 'pricing', e.target.value)}
              placeholder="e.g., $50/user/month"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      ))}

      {data.competitors.length < 10 && (
        <button
          type="button"
          onClick={addCompetitor}
          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600"
        >
          + Add Competitor
        </button>
      )}

      <div className="pt-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Competitor Documents</label>
        <FileUploadZone
          questionnaireId={questionnaireId}
          section="competitors"
          uploads={uploads}
          onUploadsChange={onUploadsChange}
        />
      </div>
    </div>
  );
}
